// MUI components
import { Chip, LinearProgress, Box, Typography } from "@mui/material";
import dayjs from "dayjs";

// Single source of truth for the real MerchantAccountStatus enum values
// (EMAIL_UNVERIFIED, ONBOARDING_NOT_STARTED, ONBOARDING_PARTIALLY_COMPLETED,
// PENDING_APPROVAL, APPROVED, REJECTED). The old table referenced a
// non-existent "ONBOARDING_COMPLETED" status and had no APPROVED case, so an
// approved merchant showed "0/4 Done" and every pending one a misleading
// "2/4 Done". `progress` is the onboarding-completeness % shown by the bar.
const MERCHANT_STATUS = {
	EMAIL_UNVERIFIED: { label: "Email Unverified", color: "#e0e0e0", progress: 0 },
	ONBOARDING_NOT_STARTED: { label: "Not Started", color: "#e0e0e0", progress: 0 },
	ONBOARDING_PARTIALLY_COMPLETED: { label: "In Process", color: "#ffc107", progress: 50 },
	PENDING_APPROVAL: { label: "Awaiting Approval", color: "#ffc107", progress: 100 },
	APPROVED: { label: "Approved", color: "#00a743", progress: 100 },
	REJECTED: { label: "Rejected", color: "#c00", progress: 100 },
};

const columns = [
	{
		headerName: "Merchant ID",
		field: "merchantId",
		renderCell: ({ row }) => <div>{row.id}</div>,
	},
	{
		headerName: "Merchant Name",
		field: "merchantName",
		renderCell: ({ row }) => {
			const info = row.contactInformation || {};
			const contactName = `${info?.firstName || ""} ${info?.lastName || ""}`.trim();
			// Fall back so the cell is never blank: contact person → company name
			// → email → id. Merchants who haven't filled contact info (onboarding
			// not started) otherwise showed an empty name and were unidentifiable.
			const display =
				contactName ||
				row.businessInformation?.companyName ||
				row.email ||
				row.id ||
				"—";
			return <div>{display}</div>;
		},
	},
	{
		headerName: "Sign Up Date",
		field: "signupDate",
		renderCell: ({ row }) => (
			<div>{dayjs(row.createdAt).format("DD MMM YYYY")}</div>
		),
	},
	{
		headerName: "Status",
		field: "status",
		renderCell: ({ row }) => {
			const meta = MERCHANT_STATUS[row.accountStatus];
			return (
				<div>
					<Chip
						label={meta?.label || row.accountStatus || "—"}
						sx={{
							backgroundColor: meta?.color || "#e0e0e0",
							color:
								meta && meta.color !== "#e0e0e0" ? "#fff" : undefined,
						}}
					/>
				</div>
			);
		},
	},
	{
		headerName: "Progress",
		field: "progress",
		renderCell: ({ row }) => {
			const meta = MERCHANT_STATUS[row.accountStatus] || {
				color: "#e0e0e0",
				progress: 0,
			};
			return (
				<Box className="flex items-center w-full h-full">
					<LinearProgress
						variant="determinate"
						value={meta.progress}
						sx={{
							flex: 1,
							height: 8,
							borderRadius: 5,
							backgroundColor: "#f5f5f5",
							"& .MuiLinearProgress-bar": { backgroundColor: meta.color },
						}}
					/>
					<Typography variant="body2" sx={{ ml: 1, minWidth: 48 }}>
						{meta.progress}%
					</Typography>
				</Box>
			);
		},
	},
];

const dataTableData = {
	columns,
};

export default dataTableData;
